import {
  type PlayerOrgRatingAllResponsePagedData,
  type PlayerRatingResponse,
  RankingType,
} from '@on3/api';
import {
  On3IconOn3IndustryRanking,
  On3IconOn3IndustryRankingJuco,
  On3IconOn3IndustryRankingsTransferPortal,
} from '@on3/icons';
import { slugify } from '@on3/ui-lib/utils/slugify';
import clsx from 'clsx';
import { EmbedButton } from 'components/EmbedButton/EmbedButton';
import { Link } from 'components/Link';
import { StarRating } from 'components/UI/StarRating/StarRating';
import useAthlete from 'contexts/AthleteProvider';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import { StrengthMeter } from './PlayerRating/StrengthMeter';
import styles from './Rankings.module.scss';
import { RankingsItem } from './RankingsItem';

interface PlayerRankingsProps {
  playerRankings?: PlayerRatingResponse | null;
  allRankingList?: PlayerOrgRatingAllResponsePagedData | null;
  comparisonLink?: string;
  embedText?: string;
}

const displayOrgType = (orgType?: string | null) => {
  if (orgType === 'JuniorCollege') {
    return 'Juco';
  } else if (orgType === 'HighSchool') {
    return 'High School';
  }

  return orgType;
};

export const Rankings = ({
  playerRankings,
  allRankingList,
  comparisonLink,
  embedText = '',
}: PlayerRankingsProps) => {
  const adjustedRankingsList = allRankingList?.list?.filter(
    (rank) =>
      rank.organization ||
      (rank?.ratings?.length &&
        rank?.ratings[0]?.rankingType === RankingType.Player),
  );
  const { athlete } = useAthlete();
  const { strength } = playerRankings || {};

  const router = useRouter();
  const sport = router.query.sport as string;
  const sportQuery = (sport && `?sport=${sport}`) || '';

  comparisonLink =
    comparisonLink ??
    `/db/${router.query.id}/industry-comparison/${sportQuery}`;

  const [rankingList, setRankingList] = useState(
    adjustedRankingsList?.[adjustedRankingsList?.length - 1],
  );
  const showTabs = adjustedRankingsList && adjustedRankingsList?.length > 1;

  const industry = rankingList?.ratings?.find(
    (rank) => rank.type === 'Consensus',
  );
  const services = rankingList?.ratings?.filter(
    (rank) => rank.type !== 'Consensus' && rank.type !== 'Industry',
  );
  const showJucoLogo = rankingList?.organization?.orgType === 'JuniorCollege';
  const linkSport = slugify(industry?.sport || 'football');
  const linkYear = industry?.rankingYear;
  const linkPosition = industry?.positionAbbr?.toLocaleLowerCase() || 'qb';
  const linkState = industry?.stateAbbr?.toLocaleLowerCase() || 'al';
  const isTransferPortalRank =
    industry?.rankingType === RankingType.TransferPortal;
  const baseLink = isTransferPortalRank
    ? `/transfer-portal/industry/${linkSport}/${linkYear}/`
    : `/db/rankings/industry-player/${linkSport}/${linkYear}/`;
  const mainLink = `${baseLink}${showJucoLogo ? '?type=juniorcollege' : ''}`;
  const positionLink = `${baseLink}?${
    showJucoLogo ? 'type=juniorcollege&' : ''
  }position=${linkPosition}`;
  const stateLink = `${baseLink}?${
    showJucoLogo ? 'type=juniorcollege&' : ''
  }state=${linkState}`;

  const isRankingsPage = router.asPath.includes('industry-comparison');

  const showEmbed = !!athlete?.name && !!athlete?.slug;

  const embedCode = `<iframe class="onthree-embed" title="${athlete?.name} Industry Comparison Embed" style="max-width: 100%" frameborder="0" width="600" height="200" src="https://embed.on3.com/db/${athlete?.slug}/industry-comparison/"></iframe>
    <script async="" src="https://on3static.com/scripts/embed.js?v=3" charset="utf-8"></script>`;

  const handleClick = useCallback(
    (index: number) => (event: React.MouseEvent<HTMLElement>) => {
      setRankingList(adjustedRankingsList?.[index]);
    },
    [adjustedRankingsList],
  );

  return (
    <section className={styles.container}>
      {showTabs && (
        <div className={styles.tabs}>
          {adjustedRankingsList?.map((rank, index) => {
            const rankingType = rank?.ratings?.[0]?.rankingType;
            const orgType = rank?.organization?.orgType;

            return (
              <button
                className={clsx(styles.button, {
                  [styles.selected]:
                    rank?.organization?.key === rankingList?.organization?.key,
                })}
                key={rank?.organization?.key}
                onClick={handleClick(index)}
                type="button"
              >
                {rankingType === RankingType.TransferPortal
                  ? 'Transfer Portal'
                  : displayOrgType(orgType)}
              </button>
            );
          })}
        </div>
      )}

      <div className={styles.header}>
        <div className={styles.industryRankingsLogo}>
          {!showJucoLogo && !isTransferPortalRank && (
            <On3IconOn3IndustryRanking height={16} />
          )}
          {isTransferPortalRank && (
            <On3IconOn3IndustryRankingsTransferPortal height={26} />
          )}
          {showJucoLogo && (
            <On3IconOn3IndustryRankingJuco height={16} width={213} />
          )}
        </div>
        <div
          className={clsx(styles.industryRankingsValues, {
            [styles.transferPortal]: isTransferPortalRank,
          })}
        >
          <span className={styles.heading}>Natl</span>
          <span className={styles.heading}>
            {industry?.positionAbbr || 'pos'}
          </span>
          {industry?.rankingType !== RankingType.TransferPortal && (
            <span className={styles.heading}>
              {industry?.stateAbbr || 'st'}
            </span>
          )}
        </div>
      </div>

      <div className={styles.industryRankWrapper}>
        {industry?.rating && (
          <>
            <div className={styles.industryRating}>
              <span className={styles.rating}>
                {industry?.rating?.toFixed(2)}
              </span>
              <StarRating href={mainLink} large stars={industry?.stars} />
              {industry?.fiveStarPlus && (
                <Link
                  className={styles.fiveStarPlus}
                  href={`/db/rankings/industry-five-stars/${linkSport}/${linkYear}/`}
                >
                  +
                </Link>
              )}
            </div>
            <div
              className={clsx(styles.industryRankingsValues, {
                [styles.transferPortal]: isTransferPortalRank,
              })}
            >
              <Link className={styles.rank} href={mainLink}>
                {industry?.overallRank}
              </Link>
              <Link className={styles.rank} href={positionLink}>
                {industry?.positionRank}
              </Link>
              {industry?.rankingType !== RankingType.TransferPortal && (
                <Link className={styles.rank} href={stateLink}>
                  {industry?.stateRank}
                </Link>
              )}
            </div>
          </>
        )}
      </div>

      {!!strength && (
        <div className={styles.strengthMeter}>
          <StrengthMeter strengthMeter={strength} />
        </div>
      )}

      <div className={styles.industryRankings}>
        <ul>
          {services?.map((service, index) => {
            if (
              service.rankingType === RankingType.TransferPortal &&
              service.type === 'Espn'
            ) {
              return null;
            }

            return (
              <RankingsItem athlete={athlete} key={index} service={service} />
            );
          })}
        </ul>
      </div>

      <div className={styles.linkContainer}>
        <Link href={comparisonLink}>Industry Comparison</Link>
        <div>
          {!isRankingsPage && (
            <Link
              href={'/news/on3-consensus-ratings-rankings/'}
              rel="noopener noreferrer"
              target="_blank"
            >
              About
            </Link>
          )}
          {showEmbed && (
            <EmbedButton
              buttonText={embedText}
              embedCode={embedCode}
              title={`${athlete?.name} Industry Comparison`}
            />
          )}
        </div>
      </div>
    </section>
  );
};
